import { useEffect, useState } from 'react'
import type { RunStep } from '../types'
import { ACTION_INFO, explainStep, stepOutcome } from '../copy/explain'
import { IconCheck, IconSpinner, IconClock, IconPhoto } from '../icons'
import { Lightbox, type Shot } from './Lightbox'

/**
 * 决策时间线：把管线选了哪些动作、为什么选，按顺序摊开给用户看。
 *
 * 背景：这条管线会自己决定走「无损切除」还是「净化后重新生成」，两条路的代价差很多。
 *   用户付费之前有权知道它凭什么这么判，看不到依据就只能赌。
 * 设计意图：每一步先说「这一步在做什么」（explainStep 用该步实测数值生成），
 *   再把「为什么走这一步」和原始读数折叠进「技术细节」。判定依据来自服务端，
 *   已经是中文（管线产出韩语，换在 lab_ripple_api 的 core/step_reason.py）。
 * 关键约束：running 只允许出现在最后一条。中间步骤转圈会让人以为管线在回头重做。
 *   进行中那一条不展开技术细节，因为此时 result 还是空的，展开只会看到一个空对象。
 */
export function DecisionTrace({ steps, running = false, fileUrl }: {
  steps: RunStep[]
  running?: boolean
  /**
   * 把这一步写下的文件名换成可以看的地址：原图一个、缩略图一个。
   * 取不到就返回 null，那个文件只报个名字。
   *
   * 由调用方给：这一层不知道作业 id，也分不清这是真作业还是那卷录像——
   * 录像里的文件名在服务端并不存在，给了链接点开就是 404。
   */
  fileUrl?: (name: string) => { src: string; thumb: string } | null
}) {
  /**
   * 这一趟所有能看的图，按步骤顺序排成一组，灯箱里左右键就在这组里翻。
   *
   * 要能来回比才有用：同一种图（对照板、热力图）在重画前后各有一张，
   * 区别只在来回切换的那一下能看出来（2026-09-11「点击还能放大到灯箱」）。
   */
  const shots: Shot[] = []
  const shotAt = new Map<string, number>()
  steps.forEach((s, i) => {
    if (running && i === steps.length - 1) return
    for (const f of stepOutcome(s).files) {
      const url = fileUrl?.(f.name)
      if (!url) continue
      shotAt.set(`${s.n}:${f.name}`, shots.length)
      shots.push({ ...url, label: f.label,
                   from: `第 ${s.n} 步 · ${ACTION_INFO[s.action]?.label ?? s.action}` })
    }
  })
  const [open, setOpen] = useState<number | null>(null)

  return (
    <div className="timeline">
      {steps.map((s, i) => {
        const isRunning = running && i === steps.length - 1
        const info = ACTION_INFO[s.action]
        const out = isRunning ? null : stepOutcome(s)
        return (
          <div key={s.n} className={`step ${isRunning ? 'run' : 'ok'}`}>
            <div className="step-dot">
              {isRunning ? <IconSpinner size={16} /> : <IconCheck size={16} />}
            </div>
            <div className="step-body">
              <div className="step-head">
                <h5>{info?.label ?? s.action}</h5>
                <span className="step-tag">第 {s.n} 步</span>
                <span className="step-tag">{s.by === 'rules' ? '规则决策' : '模型决策'}</span>
                {isRunning && <Elapsed />}
              </div>
              <p className="step-why">{explainStep(s, isRunning)}</p>
              {/* 这一步自己测出来的数，和它写下的文件。空的就整块不出现——
                  「完成」「等你决定」这两步本来就没有产出（见 stepOutcome）。 */}
              {out && out.readings.length > 0 && (
                <div className="step-out">
                  {out.readings.map(x => (
                    <span key={x.k} className="step-kv">
                      <i>{x.k}</i>
                      <b>{x.v}</b>
                    </span>
                  ))}
                </div>
              )}
              {/* 图直接给预览，点开是灯箱（2026-09-11 用户裁决）。此前这里是一行
                  文字链接，点开在新标签页里是一张裸图——看完还得切回来找原来那一步。
                  取不到地址的（录像那一卷）只报个名字：给了链接点开就是 404。 */}
              {out && out.files.length > 0 && (
                <div className="step-shots">
                  {out.files.map(f => {
                    const i = shotAt.get(`${s.n}:${f.name}`)
                    return i === undefined ? (
                      <span key={f.name} className="step-shot is-flat">
                        <IconPhoto size={13} />{f.label}
                      </span>
                    ) : (
                      <button key={f.name} className="step-shot" onClick={() => setOpen(i)}
                              title={`放大看「${f.label}」`}>
                        <img
                          src={shots[i].thumb}
                          alt=""
                          loading="lazy"
                          onError={e => {
                            // 盘的缩略图接口若因旧会话或代理异常取不到，至少退回那条
                            // 已签好的原图地址。只退一次，原图本身失败时不能无限重试。
                            const image = e.currentTarget
                            if (image.dataset.fallback) return
                            image.dataset.fallback = '1'
                            image.src = shots[i].src
                          }}
                        />
                        <span>{f.label}</span>
                      </button>
                    )
                  })}
                </div>
              )}
              {!isRunning && (
                <details className="step-raw">
                  <summary>技术细节</summary>
                  <pre>{`动作　${s.action}
判定依据　${s.reason}
${JSON.stringify(s.result, null, 2)}`}</pre>
                </details>
              )}
            </div>
          </div>
        )
      })}
      {open !== null && (
        <Lightbox shots={shots} index={open} onClose={() => setOpen(null)} />
      )}
    </div>
  )
}

/**
 * 进行中那一步的已耗时。跑着的那一屏也用它（压在图上的那条玻璃条，见 YourImage）：
 * 「只给已耗时、不给进度条」这条规矩只能有一处实现，两处各写一遍早晚会分叉。
 *
 * 背景：重新生成要走一次外部出图接口，实测几十秒；4K 图的诊断在服务器上约一分钟。
 *   页面上只有一个转圈的话，用户分不出「还在跑」和「卡死了」，
 *   于是会刷新页面重来，而刷新在真实作业里意味着白花一次米粒。
 * 设计意图：只显示已耗时，不做进度条也不给预估剩余。这一步的耗时取决于外部接口，
 *   我们无法预测；给出一个会跳变或者停在 90% 的进度条比不给更糟。
 * 关键约束：计时从组件挂载算起，不是从作业创建算起。接真实接口时应改为
 *   用服务端返回的步骤开始时间，否则中途刷新页面会把耗时清零。
 */
export function Elapsed({ className = 'step-elapsed' }: { className?: string }) {
  const [sec, setSec] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setSec(s => s + 1), 1000)
    return () => clearInterval(t)
  }, [])
  const mm = Math.floor(sec / 60)
  const ss = String(sec % 60).padStart(2, '0')
  return (
    <span className={className}>
      <IconClock size={13} />已耗时 {mm > 0 ? `${mm}:${ss}` : `${sec} 秒`}
    </span>
  )
}
